/**
 * First-claim experience: what a new ClaimHive firm sees after onboarding.
 *
 * Two contracts live here.
 *
 * 1. INBOX SCAN SIGNAL (provided by the email-to-first-claim pipeline).
 *    The UI only reads it. Until the pipeline ships the endpoint, the client
 *    derives a conservative state from the connected Gmail accounts (see
 *    client/src/hooks/use-inbox-scan.ts). The pipeline needs to provide:
 *
 *      GET /api/inbox-scan/status   (isAuthenticated, scoped to the session's firm)
 *        -> InboxScanState
 *
 *    No other client change is needed once that route answers with 200.
 *
 * 2. FIRST-RUN STATE (owned by this feature). One record per user, stored in
 *    user_settings.metadata.firstRun, so no migration is needed.
 *
 *      GET  /api/first-run                  -> FirstRunState
 *      POST /api/first-run/:key/complete    -> FirstRunState   (idempotent)
 *
 * Nothing here calls an AI model. Suggested next steps are rules over the
 * claim's own fields (client/src/lib/first-claim-next-steps.ts).
 */
import { z } from 'zod';

export const INBOX_SCAN_STATUS_API = '/api/inbox-scan/status';

export const INBOX_SCAN_STATUSES = ['not_connected', 'scanning', 'found', 'none_yet', 'error'] as const;
export type InboxScanStatus = (typeof INBOX_SCAN_STATUSES)[number];

/**
 * The pipeline's answer to "what is the inbox scan doing for this firm?".
 *
 * - not_connected: no active Gmail account for the firm.
 * - scanning:      an account is connected and the first scan is running.
 * - found:         the scan created at least one Claim; claimIds lists them, newest first.
 * - none_yet:      the scan finished (or the inbox is quiet) and no Claim was created.
 * - error:         the scan failed; the UI offers reconnect and manual entry.
 */
export interface InboxScanState {
  status: InboxScanStatus;
  /** Address being scanned, when known. Shown as "Scanning name@firm.com". */
  accountEmail?: string | null;
  /** Emails looked at so far. Drives the progress counter while scanning. */
  scannedCount?: number;
  /** Emails the scan expects to look at. Null or missing when unknown (the UI then shows an indeterminate bar). */
  totalCount?: number | null;
  /** Claims the scan created, newest first. Required for found; empty otherwise. */
  claimIds: string[];
  /** Total Claims created by the scan, even when claimIds is capped. */
  foundCount: number;
}

export const inboxScanStateSchema = z.object({
  status: z.enum(INBOX_SCAN_STATUSES),
  accountEmail: z.string().nullish(),
  scannedCount: z.number().int().nonnegative().optional(),
  totalCount: z.number().int().nonnegative().nullish(),
  claimIds: z.array(z.string()),
  foundCount: z.number().int().nonnegative(),
});

export const FIRST_RUN_API = '/api/first-run';

/** Each key is shown once per user. Add a key here to add a first-run moment. */
export const FIRST_RUN_KEYS = ['claim-view-tour', 'first-claim-reveal'] as const;
export type FirstRunKey = (typeof FIRST_RUN_KEYS)[number];

export const firstRunKeySchema = z.enum(FIRST_RUN_KEYS);

export interface FirstRunState {
  /** ISO 8601 time each completed key was finished or dismissed. A missing key has not been seen. */
  completed: Partial<Record<FirstRunKey, string>>;
}
